import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Link as MuiLink,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    Sensors as SensorsIcon,
    Warning as WarningIcon,
    SignalWifiOff as OfflineIcon,
    BatteryAlert as BatteryIcon,
    LocalFireDepartment as SmokeIcon,
    Thermostat as TempIcon,
    Water as WaterIcon,
    Co2 as CoIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/admin/PageHeader';
import { useIotDashboardQuery } from '../../api/JsdhNotifikaceService/service/IotMutations';
import type { IotDashboardResponse, IotSenzorUdalost, SenzorUdalostTyp } from '../../api/JsdhNotifikaceService/generated';
import { formatNaposledy, formatPlnyCas, popisHodnoty, tvarPoctu } from '../../components/iot/iotStav';
import { useTed } from '../../components/iot/useTed';

const getUdalostIcon = (typ: SenzorUdalostTyp) => {
    switch (typ) {
        case 'SMOKE': return <SmokeIcon color="error" />;
        case 'CO': return <CoIcon color="error" />;
        case 'HIGH_TEMP': return <TempIcon color="warning" />;
        case 'WATER_LEAK': return <WaterIcon color="info" />;
        case 'BATTERY_LOW': return <BatteryIcon color="warning" />;
        case 'FAULT': return <WarningIcon color="error" />;
        case 'OFFLINE': return <OfflineIcon color="action" />;
        default: return <SensorsIcon />;
    }
};

export const getUdalostLabel = (typ: SenzorUdalostTyp): string => {
    switch (typ) {
        case 'SMOKE': return 'Detekce kouře';
        case 'CO': return 'Detekce CO';
        case 'HIGH_TEMP': return 'Vysoká teplota';
        case 'WATER_LEAK': return 'Zaplavení';
        case 'BATTERY_LOW': return 'Nízká baterie';
        case 'FAULT': return 'Porucha čidla';
        case 'OFFLINE': return 'Čidlo nedostupné';
        default: return typ;
    }
};

/** Místo události: „Kouř garáž · Garáž · JSDH Dev objekt“ (spec UX IoT 4.9, K11b). */
export const mistoUdalosti = (u: IotSenzorUdalost): string =>
    [u.senzorNazev, u.senzorUmisteni, u.objektNazev].filter((x): x is string => !!x && !!x.trim()).join(' · ');

/** Typ události a u teploty i hodnota („Vysoká teplota 21,5 °C“); surový JSON se nezobrazí. */
export const textUdalosti = (u: IotSenzorUdalost): string => {
    const hodnota = popisHodnoty(u.typ, u.hodnota);
    return hodnota ? `${getUdalostLabel(u.typ)} ${hodnota}` : getUdalostLabel(u.typ);
};

export interface ProblemObjektu {
    objektId: number;
    nazev: string;
    poplach: boolean;
    /** Čas posledního dnešního poplachu v objektu (ms), když nějaký je. */
    posledniPoplachMs: number | null;
    /** RPi nedostupné; `od` = poslední spojení (ISO), když je známé. */
    rpi: { od: string | null } | null;
}

const jeDnes = (iso: string, now: number): boolean => {
    const d = new Date(iso);
    const t = new Date(now);
    return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
};

/**
 * Pruh problémů (4.9): jeden řádek za objekt. Poplach = dnešní událost s poplachem v objektu (stejné pravidlo
 * jako počet poplachů `aktivniPoplachy` v BE; neznamená, že poplach pořád trvá, proto se v textu píše „dnes“).
 * RPi = objekt z `objektySProblemem`: BE ho tam dává podle stáří posledního kontaktu (> 5 min), ne podle příznaku
 * `rpiOnline`, který zůstane true, když RPi přestane hlásit bez zprávy offline (review K10-K12, I1). Věříme proto
 * členství v seznamu a `rpiStatus`; jen výslovné `rpiStatus === 'online'` RPi problém ruší.
 */
export const problemyObjektu = (data: IotDashboardResponse, now: number = Date.now()): ProblemObjektu[] => {
    const mapa = new Map<number, ProblemObjektu>();
    const ziskej = (id: number, nazev: string) => {
        const p = mapa.get(id) ?? { objektId: id, nazev, poplach: false, posledniPoplachMs: null, rpi: null };
        mapa.set(id, p);
        return p;
    };
    (data.posledniUdalosti ?? []).forEach((u) => {
        if (u.poplachVytvoren && u.objektId != null && jeDnes(u.cas, now)) {
            const p = ziskej(u.objektId, u.objektNazev || `Objekt ${u.objektId}`);
            p.poplach = true;
            const ms = new Date(u.cas).getTime();
            if (!Number.isNaN(ms) && (p.posledniPoplachMs == null || ms > p.posledniPoplachMs)) p.posledniPoplachMs = ms;
        }
    });
    (data.objektySProblemem ?? []).forEach((o) => {
        // RPi, které se zatím neozvalo (bez posledního kontaktu), není nedostupné: do pruhu ani do cíle dlaždice nepatří.
        if (o.rpiStatus !== 'online' && o.rpiLastOnline) ziskej(o.id, o.nazev).rpi = { od: o.rpiLastOnline };
    });
    return Array.from(mapa.values()).sort((a, b) => Number(b.poplach) - Number(a.poplach) || a.nazev.localeCompare(b.nazev, 'cs'));
};

const hodinyMinuty = (ms: number): string => {
    const d = new Date(ms);
    return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** „Raspberry Pi nedostupné, naposledy před 12 min“ (slovník 1.1); bez známého kontaktu jen „Raspberry Pi nedostupné“. */
export const textRpi = (od: string | null, now: number = Date.now()): string => {
    const ms = od ? new Date(od).getTime() : NaN;
    return Number.isNaN(ms) ? 'Raspberry Pi nedostupné' : `Raspberry Pi nedostupné, naposledy ${formatNaposledy(ms, now)}`;
};

/** První řádek pruhu: „Dnes poplach v objektu X (poslední v 0:13)“, jinak RPi. */
export const nadpisProblemu = (p: ProblemObjektu): string => {
    if (p.poplach) return `Dnes poplach v objektu ${p.nazev}${p.posledniPoplachMs != null ? ` (poslední v ${hodinyMinuty(p.posledniPoplachMs)})` : ''}`;
    return `Raspberry Pi nedostupné v objektu ${p.nazev}`;
};

const RadekProblemu: React.FC<{ p: ProblemObjektu; ted: number }> = ({ p, ted }) => {
    const ms = p.rpi?.od ? new Date(p.rpi.od).getTime() : NaN;
    // Druhý řádek: u poplachu stav RPi, u samotného RPi čas posledního kontaktu.
    const druhy = p.poplach
        ? (p.rpi ? textRpi(p.rpi.od, ted) : null)
        : (Number.isNaN(ms) ? null : `Naposledy se ozvalo ${formatNaposledy(ms, ted)}`);
    // Proběhlý poplach (dnes) je informace, ne výzva k zásahu: nevíme, že ještě trvá (D4), proto ne červeně.
    return (
        <Alert severity={p.poplach ? 'info' : 'warning'} icon={p.poplach ? undefined : <OfflineIcon />}
            action={<Button color="inherit" size="small" component={RouterLink} to={`/admin/iot/objekty/${p.objektId}`}>Otevřít objekt</Button>}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{nadpisProblemu(p)}</Typography>
            {druhy && <Typography variant="body2">{druhy}</Typography>}
        </Alert>
    );
};

interface DlazdiceProps {
    pocet: number;
    text: string;
    /** Barva čísla jen u nenulového problému; jinak neutrální. */
    problem?: 'warning' | 'error';
    to: string;
}

const Dlazdice: React.FC<DlazdiceProps> = ({ pocet, text, problem, to }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h4" component="p" fontWeight="bold" color={problem && pocet > 0 ? `${problem}.main` : 'text.primary'}>
                    {pocet}
                </Typography>
                <Typography variant="body2" color="text.secondary">{text}</Typography>
            </CardContent>
        </CardActionArea>
    </Card>
);

const IoTDashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = useIotDashboardQuery();
    // Relativní časy („před 12 min“) se posouvají, i když 30s refetch vrací stejná data (review K10-K12, M3).
    const ted = useTed();

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refetch()}>Zkusit znovu</Button>}>
                Přehled IoT se nepodařilo načíst.
            </Alert>
        );
    }

    const problemy = problemyObjektu(data, ted);
    // Dlaždice vede do objektu jen tehdy, když právě jeden objekt má problém, který dlaždice počítá; jinak na
    // seznam objektů (bug-inspector P2). U nedostupných čidel objekt neznáme, vedou vždy na seznam.
    const cil = (sProblemem: ProblemObjektu[]) => (sProblemem.length === 1 ? `/admin/iot/objekty/${sProblemem[0].objektId}` : '/admin/iot/objekty');
    const cilPoplachu = cil(problemy.filter((p) => p.poplach));
    const cilRpi = cil(problemy.filter((p) => p.rpi));
    const nedostupnaCidla = data.senzoryOffline ?? 0;
    const udalosti = (data.posledniUdalosti ?? []).filter((u) => u.typ !== 'HEARTBEAT').slice(0, 10);
    const n = (x: number | undefined) => x ?? 0;

    return (
        <Box>
            <PageHeader
                title="Přehled IoT"
                icon={<SensorsIcon />}
                action={
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => navigate('/admin/iot/objekty')}>Objekty</Button>
                        <Button variant="outlined" onClick={() => navigate('/admin/iot/zarizeni')}>Evidence zařízení</Button>
                    </Stack>
                }
            />

            {problemy.length > 0 && (
                <Stack spacing={1} sx={{ mb: 3 }} data-testid="pruh-problemu">
                    {problemy.map((p) => <RadekProblemu key={p.objektId} p={p} ted={ted} />)}
                </Stack>
            )}
            {/* Dnešní poplachy bez známého objektu (např. smazané čidlo): aspoň obecný řádek, ať poplach nezapadne. */}
            {n(data.aktivniPoplachy) > 0 && !problemy.some((p) => p.poplach) && (
                <Alert severity="info" sx={{ mb: 3 }}
                    action={<Button color="inherit" size="small" component={RouterLink} to="/admin/iot/objekty">Objekty</Button>}>
                    {`Dnes ${tvarPoctu(n(data.aktivniPoplachy), 'byl', 'byly', 'bylo')} ${n(data.aktivniPoplachy)} ${tvarPoctu(n(data.aktivniPoplachy), 'poplach', 'poplachy', 'poplachů')} z čidel.`}
                </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid size={{ xs: 6, md: 4 }}>
                    <Dlazdice pocet={n(data.objektyCount)} text={tvarPoctu(n(data.objektyCount), 'objekt', 'objekty', 'objektů')} to="/admin/iot/objekty" />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                    <Dlazdice pocet={n(data.senzoryCount)} text={tvarPoctu(n(data.senzoryCount), 'čidlo', 'čidla', 'čidel')} to="/admin/iot/objekty" />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                    <Dlazdice pocet={nedostupnaCidla} problem="warning" to="/admin/iot/objekty"
                        text={tvarPoctu(nedostupnaCidla, 'nedostupné čidlo', 'nedostupná čidla', 'nedostupných čidel')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                    <Dlazdice pocet={n(data.aktivniPoplachy)} problem="error" to={cilPoplachu}
                        text={tvarPoctu(n(data.aktivniPoplachy), 'poplach dnes', 'poplachy dnes', 'poplachů dnes')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                    <Dlazdice pocet={n(data.offlineRpi)} problem="warning" to={cilRpi} text="bez spojení s Raspberry Pi" />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                    <Dlazdice pocet={n(data.nizkaBaterie)} problem="warning" to="/admin/iot/objekty"
                        text={tvarPoctu(n(data.nizkaBaterie), 'čidlo s nízkou baterií', 'čidla s nízkou baterií', 'čidel s nízkou baterií')} />
                </Grid>
            </Grid>

            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                    Poslední události
                </Typography>
                {udalosti.length ? (
                    <List dense>
                        {udalosti.map((u) => {
                            const misto = mistoUdalosti(u);
                            const cas = new Date(u.cas).getTime();
                            return (
                                <ListItem key={u.id} data-testid={'udalost-' + u.id}
                                    secondaryAction={u.poplachVytvoren && (u.poplachId != null ? (
                                        <MuiLink component={RouterLink} to={`/admin/udalosti/${u.poplachId}`} variant="body2">
                                            Poplach č. {u.poplachId}
                                        </MuiLink>
                                    ) : <Chip label="Poplach" color="error" size="small" />)}
                                    sx={{ pr: u.poplachVytvoren ? 14 : 2 }}>
                                    <ListItemIcon>{getUdalostIcon(u.typ)}</ListItemIcon>
                                    <ListItemText
                                        primary={textUdalosti(u)}
                                        secondary={(
                                            <>
                                                {misto && <Box component="span" sx={{ display: 'block' }}>{misto}</Box>}
                                                <Box component="span" title={Number.isNaN(cas) ? undefined : formatPlnyCas(cas)}>
                                                    {Number.isNaN(cas) ? u.cas : formatNaposledy(cas, ted)}
                                                </Box>
                                                {u.mutovano && <Box component="span"> · ztlumeno</Box>}
                                            </>
                                        )}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Zatím žádné události z čidel.
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default IoTDashboardPage;
